import { FC, Suspense, useCallback, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { SWRConfig, SWRConfiguration } from 'swr';
import { swrFetcherLogminer } from 'packages/lib/utils/fetch';
import {
  allSearchResultsAtom,
  searchPageNoAtom,
  searchPageSizeAtom,
} from 'packages/omnitag/state';
import { errorBoundaryFallback } from 'packages/lib/common-ui/errorBoundaryFallback';
import { useResetAtom } from 'jotai/utils';
import PolarisLayout from 'packages/polaris/tool/layout';

const swrGlobalConfig: Partial<SWRConfiguration> = {
  errorRetryCount: 1,
  fetcher: swrFetcherLogminer,
  revalidateOnFocus: false,
  suspense: false,
  onError: (err) => console.error(err),
};

export const PolarisRoot: FC<React.PropsWithChildren<unknown>> = () => {
  const resetSearchPageSizeAtom = useResetAtom(searchPageSizeAtom);
  const resetSearchPageNoAtom = useResetAtom(searchPageNoAtom);
  const resetAllSearchResults = useResetAtom(allSearchResultsAtom);

  const resetState = useCallback(() => {
    resetSearchPageNoAtom();
    resetSearchPageSizeAtom();
    resetAllSearchResults();
  }, [resetSearchPageNoAtom, resetSearchPageSizeAtom, resetAllSearchResults]);

  // Recoil atoms should be reset when leaving the tool, so they're at their default values if/when the user returns later
  useEffect(
    () => {
      return () => {
        resetState();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      /* Run-once */
    ]
  );

  return (
    <Suspense fallback={null}>
      <Sentry.ErrorBoundary
        beforeCapture={(scope): void => {
          scope.setTag('location', 'Omnitag root');
        }}
        fallback={errorBoundaryFallback}
      >
        <SWRConfig value={swrGlobalConfig}>
          <PolarisLayout />
        </SWRConfig>
      </Sentry.ErrorBoundary>
    </Suspense>
  );
};

export default PolarisRoot;
